// Typst's pages drawn on canvases: every line where Typst set it (TypstKit's layout: baseline, size, face, and each word
// space, so justified lines are drawn justified), in the faces the PDF uses. The browser has no PDF renderer the page's
// policy allows (a frame of the PDF would need `frame-src blob:` and a plugin; pdf.js isn't allowed), and the layout is
// Typst's own, so this is the proof exactly as set, less images and rules. Each line's runs (TypstKit's flag 2) are drawn
// where Typst set them, in their own style. Pages are drawn as they scroll into view.
// Book presets show as spreads (the first page alone on the right). "Open the PDF" shows the real file.
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { FONT_FILES } from '@writing-suite/typst';
import type { LaidOutPage } from '../publish/protocol.ts';
import { typstFonts } from '../publish/client.ts';

/** The face a line names, as registered here (prefixed, so a system font of the same name never stands in). */
const faceName = (family: string) => `WS ${family}`;
const loaded = new Map<string, Promise<boolean>>();

/** `FONT_FILES` lists each family's faces in this order. */
const FACES: readonly FontFaceDescriptors[] = [
  { style: 'normal', weight: '400' }, { style: 'italic', weight: '400' }, { style: 'normal', weight: '700' }, { style: 'italic', weight: '700' },
];

/** Registers each family's faces the pages use (from the Typst fonts): regular, italic, bold, bold italic. */
function loadFaces(families: Iterable<string>): Promise<unknown> {
  const all: Promise<boolean>[] = [];
  for (const family of families) {
    (FONT_FILES[family] ?? []).forEach((file, i) => {
      const key = `${family}|${file}`;
      let p = loaded.get(key);
      if (!p) {
        p = typeof FontFace === 'undefined' || !FACES[i]
          ? Promise.resolve(false)
          : typstFonts()(file).then(async (bytes) => {
              const face = new FontFace(faceName(family), bytes.buffer as ArrayBuffer, FACES[i]);
              await face.load();
              document.fonts.add(face);
              return true;
            }).catch(() => false);
        loaded.set(key, p);
      }
      all.push(p);
    });
  }
  return Promise.all(all);
}

/** Draws `text` from `x`, each word at the start Typst gave it (after each word space in `gaps`, as [x, width]). */
function drawWords(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, gaps: number[][]) {
  const words = text.split(' ');
  if (gaps.length !== words.length - 1 || words.length < 2) {
    ctx.fillText(text, x, y);
    return;
  }
  words.forEach((w, i) => {
    if (w) ctx.fillText(w, x, y);
    const gap = gaps[i];
    if (gap) x = gap[0] + gap[1];
  });
}

function draw(canvas: HTMLCanvasElement, page: LaidOutPage, width: number) {
  const scale = width / page.width;
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  canvas.width = Math.round(page.width * scale * dpr);
  canvas.height = Math.round(page.height * scale * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, page.width, page.height);
  ctx.fillStyle = '#1f1b17';
  ctx.textBaseline = 'alphabetic';
  for (const line of page.lines) {
    if (line.runs?.length) {
      // Each run where Typst set it, in its own style: columns sharing a baseline (a manuscript's contact block and
      // word count) stay apart, and italic and bold show.
      for (const run of line.runs) {
        ctx.font = `${run.italic ? 'italic ' : ''}${run.weight >= 600 ? 700 : 400} ${run.size}px "${faceName(run.font)}", serif`;
        drawWords(ctx, run.text, run.x, line.y, run.gaps);
      }
    } else {
      ctx.font = `${line.size}px "${faceName(line.font)}", serif`;
      drawWords(ctx, line.text, line.x, line.y, line.gaps);
    }
  }
}

const PageCanvas = memo(function PageCanvas({ page, index, width, ready, current }: { page: LaidOutPage; index: number; width: number; ready: boolean; current: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return setVisible(true);
    const o = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && setVisible(true), { rootMargin: '600px' });
    o.observe(el);
    return () => o.disconnect();
  }, []);
  useEffect(() => {
    if (!visible || !ready || !ref.current) return;
    draw(ref.current, page, width);
    setDrawn(true);
  }, [visible, ready, page, width]);
  useEffect(() => {
    if (current) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [current]);
  return (
    <canvas
      ref={ref}
      className={`proof-page${current ? ' current' : ''}`}
      data-page={index + 1}
      data-drawn={drawn ? 'true' : 'false'}
      style={{ width: `${width}px`, height: `${(page.height / page.width) * width}px` }}
      width={Math.round(width)}
      height={Math.round((page.height / page.width) * width)}
      role="img"
      aria-label={`Page ${index + 1}`}
    />
  );
});

/** `pageWidth` is the widest a page is drawn; narrower when the stage is (a spread's two pages share it). */
export function LaidOutPages({ pages, book, pageWidth: widest, current }: { pages: LaidOutPage[]; book: boolean; pageWidth: number; current?: number | null }) {
  const families = useMemo(() => new Set(pages.flatMap((p) => p.lines.flatMap((l) => [l.font, ...(l.runs ?? []).map((r) => r.font)]))), [pages]);
  const box = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState<number | null>(null);
  useEffect(() => {
    // The stage that scrolls the pages (less its padding), else whatever holds them.
    const el = (box.current?.closest('.publish-proof') ?? box.current?.parentElement) as HTMLElement | null | undefined;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const style = getComputedStyle(el);
      setRoom(Math.floor(el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 4));
    };
    measure();
    const o = new ResizeObserver(measure);
    o.observe(el);
    return () => o.disconnect();
  }, []);
  const pageWidth = Math.max(120, Math.min(widest, room === null ? widest : book ? room / 2 : room));
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let live = true;
    setReady(false);
    void loadFaces(families).then(() => live && setReady(true));
    return () => {
      live = false;
    };
  }, [families]);
  // Spreads: the first page alone on the right, then verso | recto.
  const rows: (number | null)[][] = [];
  if (book) {
    rows.push([null, 0]);
    for (let i = 1; i < pages.length; i += 2) rows.push([i, i + 1 < pages.length ? i + 1 : null]);
  } else {
    for (let i = 0; i < pages.length; i++) rows.push([i]);
  }
  return (
    <div ref={box} className={`proof${book ? ' book' : ''}`} data-pages={pages.length} data-ready={ready ? 'true' : 'false'}>
      {rows.map((row, r) => (
        <div className="proof-row" key={r}>
          {row.map((i, k) =>
            i === null ? (
              <div className="proof-blank" key={`b${k}`} style={{ width: `${pageWidth}px` }} aria-hidden="true" />
            ) : (
              <PageCanvas key={i} page={pages[i]} index={i} width={pageWidth} ready={ready} current={current === i + 1} />
            ),
          )}
        </div>
      ))}
    </div>
  );
}
